import { types } from './mutations';
import debounce from '@/helpers/debounce';
import { CancelToken } from 'axios';

const BATCH_STATE = {
  0: 'Unknown',
  1: 'Ready',
  2: 'Running',
  3: 'Done',
  4: 'Error',
  5: 'TimedOut',
  6: 'CancelPartial',
};

let doDebouncedRequests = { requerstType: {}, cancelToken: {} };

const SAVING_STATE = {
  SAVING: 'topBar.saving',
  SAVED: 'topBar.updatesSaved',
  ERROR: 'topBar.errorSaving',
};

export const actions = {
  async 'resumes.fetch'({ commit, getters }, { workspace, pageNumber, sortBy }) {
    if (getters.isFetching('resumes')) {
      console.warn('resumes is already fetching from API');
      return;
    }
    commit(types.SET_FETCH, { type: 'resumes', isFetching: true });
    const result = await this.$axios.$get(
      `resumes?workspaceType=${workspace}&PageNumber=${pageNumber}&PageSize=15&sortBy=${sortBy}`
    );
    commit(types.SET_RESUMES, result);
    commit(types.SET_FETCH, { type: 'resumes', isFetching: false });
    return result;
  },
  async 'resumes.metrics'({ commit }) {
    const result = await this.$axios.$get(`resumes/metrics`);
    commit(types.SET_METRICS, result);
    return result;
  },
  async 'resumes.setMode'({ commit }, { currentMode }) {
    commit(types.SET_MODE, currentMode);
  },
  async 'resumes.getCurrent'({ commit }, id) {
    commit(types.SET_CURRENT_RESUME, null);
    const result = await this.$axios.$get(`resumes/${id}`);
    commit(types.SET_CURRENT_RESUME, result);
    return result;
  },
  async 'resumes.getGroup'(_, resumeId) {
    return await this.$axios.$get(`resumes/${resumeId}/group`);
  },
  async 'resumes.create'({ commit }, resume) {
    resume.isOrganization = !!resume.isOrganization;
    const result = await this.$axios.$post(`resumes`, resume);
    commit(types.SET_CURRENT_RESUME, result);
    return result;
  },
  async 'resumes.duplicate'({ commit }, { id, jobTitle, referenceLink, isOrganization, labels, lastname, firstname }) {
    return await this.$axios.$post(`resumes/${id}/duplicate`, {
      jobTitle,
      referenceLink,
      isOrganization,
      labels,
      lastname,
      firstname,
    });
  },
  async 'resumes.delete'({ commit, dispatch }, id) {
    commit(types.DELETE_RESUME, id);
    const result = await this.$axios.$delete(`resumes/${id}`);
    dispatch('resumes.metrics');
    return result;
  },
  async 'resumes.share'({ commit }, { resumeId, email, permission }) {
    commit(types.SET_EMAIL_NOTIFICATION, email);
    return await this.$axios.$post(`resumes/${resumeId}/share`, {
      email,
      permission,
    });
  },
  async 'resume.unshare'(_, { resumeId, userId }) {
    return await this.$axios.$delete(`/resumes/${resumeId}/share?userId=${userId}`);
  },
  async 'resumes.share.getById'(_, { resumeId }) {
    return await this.$axios.$get(`resumes/${resumeId}/share`);
  },
  async 'resumes.labels.edit'(_, { resumeId, labels }) {
    return await this.$axios.$patch(`resumes/${resumeId}`, { labels });
  },
  async 'resumes.organization.edit'({ commit }, { resumeId, organizationId }) {
    return await this.$axios.$patch(`resumes/${resumeId}`, { organizationId });
  },
  async 'resumes.candidate.edit'({ commit }, { resumeId, candidate }) {
    commit(types.UPDATE_CURRENT_RESUME, { id: resumeId, candidate });
    return await this.$axios.$patch(`resumes/${resumeId}`, {
      candidate,
    });
  },
  async 'resumes.template.edit'({ commit }, { resumeId, template }) {
    commit(types.UPDATE_CURRENT_RESUME_TEMPLATE, { resumeId, template });
    return await this.$axios.$patch(`resumes/${resumeId}`, {
      template,
    });
  },
  async 'resumes.section.title.edit'({ commit, getters }, { sectionId, title }) {
    let titles = getters.currentResume.items.find(item => item.type === 'titles');
    if (titles) {
      titles.content[sectionId] = title;
      titles.content = JSON.stringify(titles.content);
      commit(types.UPDATE_ITEM, titles);
      await this.$axios.$patch(`items/${titles.id}`, {
        content: JSON.stringify(titles.content),
      });
    } else {
      titles = { type: 'titles', content: {} };
      titles.content[sectionId] = title;
      titles.content = JSON.stringify(titles.content);
      const result = await this.$axios.$post(`resumes/${getters.currentResume.id}/items`, [titles]);
      commit(types.SET_ITEM, {
        resumeId: getters.currentResume.id,
        item: result[0],
      });
    }
  },
  async 'resumes.template.change'({ commit, getters }, { resumeId, templateId }) {
    const template = getters.templates.find(t => t.id === templateId);
    commit(types.UPDATE_CURRENT_RESUME, { id: resumeId, template });
    return await this.$axios.$patch(`resumes/${resumeId}`, {
      template,
    });
  },
  async 'resumes.download_docx'(_, id) {
    try {
      return await this.$axios.$get(`resumes/${id}/docx`, {
        responseType: 'blob',
      });
    } catch (e) {
      throw new Error('Failed to download docx');
    }
  },
  async 'resumes.download_pdf'(_, id) {
    await this.$axios.$post(`resumes/${id}/print`);

    let stop = false;

    const fetch = async resolve => {
      try {
        const result = await this.$axios.$get(`resumes/${id}/print`, {
          responseType: 'blob',
        });
        resolve(result);
      } catch (e) {
        // file not ready yet
        if (!stop) {
          setTimeout(fetch, 1000, resolve);
        } else {
          throw new Error('timeout exceded');
        }
      }
    };

    setTimeout(() => {
      stop = true;
    }, 30000);

    return await new Promise(resolve => {
      fetch(resolve);
    });
  },
  async 'items.create'({ commit }, { resumeId, item }) {
    item.content = JSON.stringify(item.content);
    const result = await this.$axios.$post(`resumes/${resumeId}/items`, [item]);
    commit(types.SET_UNDO, {
      itemId: result[0].id,
      actionType: 'Create',
      item: result[0],
    });
    commit(types.SET_ITEM, { resumeId, item: result[0] });
    return result[0];
  },
  async 'items.edit'({ commit }, item) {
    commit(types.SET_UNDO, { itemId: item.id, actionType: 'Edit', item });
    commit(types.UPDATE_ITEM, item);
    commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVING);
    const result = await this.$axios.$patch(`items/${item.id}`, item);
    commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVED);
    return result;
  },
  async 'items.content.edit'({ commit, dispatch }, { itemId, content }) {
    if (!doDebouncedRequests.requerstType[itemId]) {
      doDebouncedRequests.requerstType[itemId] = debounce((method, ...args) => {
        if (doDebouncedRequests.cancelToken[itemId]) {
          doDebouncedRequests.cancelToken[itemId].cancel('Cancel debounced request');
        }
        doDebouncedRequests.cancelToken[itemId] = CancelToken.source();
        return this.$axios['$' + method](...[...args, { cancelToken: doDebouncedRequests.cancelToken[itemId].token }]);
      }, 1000);
    }

    commit(types.SET_UNDO, { itemId, actionType: 'Edit', content });
    commit(types.SET_ITEM_CONTENT, { itemId, content });
    const contentString = JSON.stringify(content);
    commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVING);

    if (contentString.length >= 4000) {
      dispatch('toaster/addSectionTooLongToast', { root: true });
      commit(types.SET_SAVING_RESUME, SAVING_STATE.ERROR);
    } else {
      try {
        const result = await doDebouncedRequests.requerstType[itemId]('patch', `items/${itemId}`, {
          content: contentString,
        });
        commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVED);
        return result;
      } catch (e) {
        if (e.message !== 'Canceled' && e.message !== 'Cancel debounced request') {
          console.log(e);
        }
      }
    }
  },
  async 'items.undo'({ commit, getters }) {
    if (getters.undo.length <= 0) return;
    const redo = JSON.parse(JSON.stringify(getters.undo[getters.undo.length - 1]));
    commit(types.DO_UNDO);
    switch (redo.actionType) {
      case 'Edit':
        await this.$axios.$patch(`items/${redo.itemId}`, {
          content: JSON.stringify(redo.item.content),
          order: redo.item.order,
        });
        break;
      case 'Create':
        await this.$axios.$delete(`items/${redo.itemId}`);
        break;
      case 'Delete':
        const newItem = await this.$axios.$post(`resumes/${redo.resumeId}/items`, [
          { ...redo.item, content: JSON.stringify(redo.item.content) },
        ]);
        commit(types.UPDATE_ITEM_ID, {
          previousId: redo.item.id,
          newId: newItem[0].id,
        });
        break;
      default:
        console.error('undo type : ' + redo.actionType + ', is not implemented');
    }
  },
  async 'items.redo'({ commit, getters }) {
    if (getters.redo.length <= 0) return;
    const undo = JSON.parse(JSON.stringify(getters.redo[getters.redo.length - 1]));
    commit(types.DO_REDO);
    switch (undo.actionType) {
      case 'Edit':
        await this.$axios.$patch(`items/${undo.itemId}`, {
          content: JSON.stringify(undo.item.content),
        });
        break;
      case 'Create':
        const newItem = await this.$axios.$post(`resumes/${undo.resumeId}/items`, [
          { ...undo.item, content: JSON.stringify(undo.item.content) },
        ]);
        commit(types.UPDATE_ITEM_ID, {
          previousId: undo.item.id,
          newId: newItem[0].id,
        });
        break;
      case 'Delete':
        await this.$axios.$delete(`items/${undo.itemId}`);
        break;
      default:
        console.error('undo type : ' + undo.actionType + ', is not implemented');
    }
  },
  async 'items.resetUndoRedo'({ commit }, id) {
    commit(types.RESET_UNDO_REDO);
  },
  async 'items.delete'({ commit }, id) {
    commit(types.SET_UNDO, { itemId: id, actionType: 'Delete', item: {} });
    commit(types.DELETE_ITEM, id);
    commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVING);
    const result = await this.$axios.$delete(`items/${id}`);
    commit(types.SET_SAVING_RESUME, SAVING_STATE.SAVED);
    return result;
  },
  async 'labels.fetch'({ commit, getters }, { workspace, search }) {
    if (getters.isFetching('labels')) {
      console.warn('labels are already being fetched from API');
      return;
    }
    commit(types.SET_FETCH, { type: 'labels', isFetching: true });

    const result =
      search !== '' ? await this.$axios.$get(`labels?workspaceType=${workspace}&search=${search.trim()}`) : [];

    commit(types.SET_FETCH, { type: 'labels', isFetching: false });
    return result;
  },
  async 'templates.fetch'({ commit, getters }) {
    if (getters.isFetching('templates')) {
      console.warn('templates is already fetching from API');
      return [];
    }
    commit(types.SET_FETCH, { type: 'templates', isFetching: true });
    const result = await this.$axios.$get(`templates`);
    commit(types.SET_TEMPLATES, result);
    commit(types.SET_FETCH, { type: 'templates', isFetching: false });
    return result;
  },
  async 'contacts.fetch'({ commit, getters }) {
    if (getters.isFetching('contacts')) {
      console.warn('contacts is already fetching from API');
      return;
    }
    commit(types.SET_FETCH, { type: 'contacts', isFetching: true });
    const result = await this.$axios.$get(`contacts`);
    commit(types.SET_CONTACTS, result);
    commit(types.SET_FETCH, { type: 'contacts', isFetching: false });
    return result;
  },
  async 'resume.contact.edit'({ commit }, { contactId, data }) {
    const result = await this.$axios.$patch(`contacts/${contactId}`, data);
    commit(types.UPDATE_CURRENT_RESUME_CONTACT, result);
    return result;
  },

  async 'sharing.get'(_, token) {
    const result = await this.$axios.$put(`share/${token}`);
    return result;
  },
  async 'resume.template.setColor'({ commit }, palette) {
    commit(types.SET_ACTIVE_COLOR_PALETTE, palette);
  },
  async 'pannel.left.setWidth'({ commit }, width) {
    commit(types.SET_LEFT_PANEL_WIDTH, width);
  },
  async 'resume.search'({ commit, getters }, { search, pageNumber }) {
    commit(types.SET_FETCH, { type: 'resumes', isFetching: true });
    const result = await this.$axios.$get(
      `/resumes/search?query=${encodeURIComponent(search)}&PageNumber=${pageNumber}&PageSize=15`
    );
    commit(types.SET_SEARCH_RESULT, result);
    commit(types.SET_FETCH, { type: 'resumes', isFetching: false });
    return result;
  },
  async 'resume.resetSearch'({ commit }) {
    commit(types.SET_RESET_SEARCH);
  },
  async 'resumes.scroll'({ commit }, { workspace, pageNumber, sortBy }) {
    commit(types.SET_FETCH, { type: 'scroll-resumes', isFetching: true });
    const result = await this.$axios.$get(
      `resumes?workspaceType=${workspace}&PageNumber=${pageNumber}&PageSize=15&sortBy=${sortBy}`
    );
    commit(types.SET_RESUMES_SCROLL, result);
    commit(types.SET_FETCH, { type: 'scroll-resumes', isFetching: false });
  },
  async 'search.scroll'({ commit }, { search, pageNumber }) {
    commit(types.SET_FETCH, { type: 'scroll-resumes', isFetching: true });
    const result = await this.$axios.$get(
      `/resumes/search?query=${encodeURIComponent(search)}&PageNumber=${pageNumber}&PageSize=15`
    );
    commit(types.SET_SEARCH_RESUMES_SCROLL, result);
    commit(types.SET_FETCH, { type: 'scroll-resumes', isFetching: false });
  },

  async 'resumes.getBatchId'() {
    try {
      const result = await this.$axios.$post('resumes/BatchImports');
      return result.id;
    } catch (_) {
      return null;
    }
  },

  async 'resumes.addResumeToBatch'(_, { batchId, file }) {
    try {
      const formData = new FormData();
      formData.append('ResumeFile', file);
      const result = await this.$axios.$post(`resumes/BatchImports/${batchId}`, formData);
      return result.id;
    } catch (_) {
      return null;
    }
  },

  async 'resumes.removeResumeFromBatch'(_, { batchId, fileId }) {
    try {
      await this.$axios.$delete(`resumes/BatchImports/${batchId}/resume/${fileId}`);
      return true;
    } catch (_) {
      return false;
    }
  },

  async 'resumes.startResumesParsing'({ dispatch, state }, { batchId, templateOptions, organizationId }) {
    try {
      await this.$axios.$post(`resumes/BatchImports/${batchId}/LaunchParsing`, { templateOptions, organizationId });
      state.lastOrganizationId = organizationId;
      state.lastTemplateOptions = templateOptions;
      state.batchId = batchId;
      return true;
    } catch (e) {
      dispatch('toaster/parsingHasFailed', { root: true });
      return false;
    }
  },

  async 'resumes.retryResumesParsing'({ dispatch, state }) {
    try {
      await this.$axios.$post(`resumes/BatchImports/${state.batchId}/LaunchParsing`, {
        templateOptions: state.lastTemplateOptions,
        organizationId: state.lastOrganizationId,
      });
    } catch (e) {
      dispatch('toaster/parsingHasFailed', { root: true });
      return false;
    }
  },

  async 'resumes.translate'({ commit }, { id, source, target, isOrganization }) {
    return await this.$axios.$post(`resumes/${id}/translate`, {}, { params: { source, target, isOrganization } });
  },

  async getBatchProgress({ state, dispatch }) {
    try {
      state.fetchingBatchError = false;
      const result = await this.$axios.$get(`resumes/BatchImports/${state.batchId}`);
      state.batchInfo = result;

      switch (BATCH_STATE[result.status]) {
        case 'Unknown':
        case 'Error':
        case 'TimedOut':
        case 'CancelPartial':
          state.batchInfo = null;
          state.batchId = null;
          dispatch('toaster/parsingHasFailed', { root: true });
          break;
        case 'Done':
          setTimeout(() => {
            dispatch('resumes.fetch', { workspace: 2, pageNumber: 0, sortBy: '-UpdatedAt' });
            dispatch('resumes.metrics');
          }, 1000);
          setTimeout(() => {
            state.batchInfo = null;
            state.batchId = null;
          }, 3000);
          break;
        default:
          if (state.batchId) setTimeout(() => dispatch('getBatchProgress'), 1000);
          break;
      }
    } catch (e) {
      console.error(e);
      state.fetchingBatchError = true;
    }
  },
};
